import type { ReactNode } from "react";

import { ErrorState, Panel, Skeleton, Stack } from "@/components/ui";

import { clockStamp, errorText } from "../lib/present";

/** The half of a query result a panel reads. Narrowed so one shape serves every screen. */
export interface PanelQuery<T> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
  readonly dataUpdatedAt: number;
  readonly refetch: () => void;
}

export interface QueryPanelProps<T> {
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: ReactNode;
  readonly rows?: number;
  readonly query: PanelQuery<T>;
  readonly children: (data: T) => ReactNode;
}

/** One panel, three answers: a skeleton at its own shape, a named failure, or the data. */
export function QueryPanel<T>({
  title,
  subtitle,
  action,
  rows,
  query,
  children,
}: QueryPanelProps<T>) {
  const count = rows ?? 3;

  if (query.isPending) {
    return (
      <Panel title={title} action={action}>
        <Stack gap={3}>
          {Array.from({ length: count }, (_unused, index) => (
            <Skeleton key={index} height="32px" />
          ))}
        </Stack>
      </Panel>
    );
  }

  if (query.data === undefined) {
    return (
      <Panel title={title} action={action}>
        <ErrorState
          headline="Read failed"
          code={errorText(query.error)}
          retryLabel="Retry"
          onRetry={query.refetch}
        />
      </Panel>
    );
  }

  return (
    <Panel
      title={title}
      subtitle={
        query.isError ? `read at ${clockStamp(query.dataUpdatedAt)}` : (subtitle ?? undefined)
      }
      action={action}
    >
      {children(query.data)}
    </Panel>
  );
}
